import { ArrowLeft, Download, Icon, Search, X } from "@norican/icons";
import { Alert, Button, Field, Textarea, Typography } from "@norican/react";
import { useState } from "react";
import { Link } from "react-router";

import { AppShell } from "@/components/app-shell";
import { apiStatus, type CardProfile, useResolveCards } from "@/lib/api";
import { downloadBlob } from "@/lib/downloads";
import { PRINT_MAX, printAddresses, printPackage } from "@/lib/print-package";

/**
 * `/colleagues/print` — the cards of a print run in one ZIP: a QR file per
 * colleague and a Data Merge file with the card texts, both from the same
 * directory record (see print-package.ts). Colleagues are given by e-mail
 * address only, never by name.
 */
export function PrintExportPage() {
  const [text, setText] = useState("");
  const [cards, setCards] = useState<CardProfile[]>([]);
  const [notFound, setNotFound] = useState<string[]>([]);
  const [status, setStatus] = useState("");
  const resolve = useResolveCards();
  const addresses = printAddresses(text);
  const tooMany = addresses.length > PRINT_MAX;

  const find = () => {
    setStatus("");
    resolve.mutate(addresses, {
      onSuccess: (result) => {
        setCards(result?.cards ?? []);
        setNotFound(result?.not_found ?? []);
      },
    });
  };

  const download = () => {
    const created = new Date();
    downloadBlob(
      printPackage(cards, created),
      `norican-business-cards-${created.toISOString().slice(0, 10)}.zip`,
    );
    setStatus(
      `Print package with ${cards.length} ${cards.length === 1 ? "card" : "cards"} downloaded.`,
    );
  };

  return (
    <AppShell>
      <div className="mx-auto flex max-w-2xl flex-col gap-6">
        <div>
          <Button
            variant="ghost"
            tone="neutral"
            size="sm"
            render={<Link to="/colleagues" />}
            startIcon={<Icon icon={ArrowLeft} size={16} />}
          >
            Back to search
          </Button>
        </div>
        <div>
          <Typography as="h1" variant="heading-2">
            Print export
          </Typography>
          <Typography variant="body-sm" tone="muted">
            QR codes and card texts for printed business cards, for several colleagues at once. The
            ZIP holds one QR file per colleague and a data file for the InDesign business card
            template, both from the same directory profile, so the printed text and the QR always
            agree.
          </Typography>
        </div>

        <Field
          label="E-mail addresses"
          hint={`One per line or separated by semicolons, e.g. a column copied from Excel or the recipients of an Outlook mail. Only e-mail addresses are used. Up to ${PRINT_MAX}.`}
          error={
            tooMany
              ? `${addresses.length} addresses; split the list, at most ${PRINT_MAX} per export.`
              : undefined
          }
        >
          <Textarea
            rows={8}
            value={text}
            placeholder={"erika.mustermann@norican.com\nmax.mustermann@norican.com"}
            onChange={(event) => setText(event.target.value)}
          />
        </Field>
        <div>
          <Button
            tone="accent"
            disabled={addresses.length === 0 || tooMany || resolve.isPending}
            onClick={find}
            startIcon={<Icon icon={Search} size={16} />}
          >
            {resolve.isPending
              ? "Looking up…"
              : `Find ${addresses.length || ""} ${addresses.length === 1 ? "address" : "addresses"}`}
          </Button>
        </div>

        {resolve.error && (
          <Alert tone="danger" title="Lookup failed">
            {apiStatus(resolve.error) === 503
              ? "The directory is not reachable right now. Try again in a moment."
              : `Check the list: e-mail addresses only, at most ${PRINT_MAX}.`}
          </Alert>
        )}

        {notFound.length > 0 && (
          <Alert tone="warning" title={`${notFound.length} not found`}>
            No business card for: {notFound.join(", ")}
          </Alert>
        )}

        {cards.length > 0 && (
          <div className="flex flex-col gap-3">
            <Typography as="h2" variant="heading-4">
              Ready to print: {cards.length}
            </Typography>
            <ul className="bg-bg border-border divide-border divide-y overflow-hidden rounded-lg border">
              {cards.map((card) => (
                <li key={card.id} className="flex items-center gap-3 px-4 py-2">
                  <div className="min-w-0 flex-1">
                    <Typography variant="body" truncate>
                      {card.email}
                    </Typography>
                    {card.job_title && (
                      <Typography variant="body-sm" tone="muted" truncate>
                        {card.job_title}
                      </Typography>
                    )}
                  </div>
                  <Button
                    iconOnly
                    variant="ghost"
                    tone="neutral"
                    size="sm"
                    aria-label={`Remove ${card.email ?? "card"}`}
                    onClick={() => setCards((current) => current.filter((c) => c.id !== card.id))}
                  >
                    <Icon icon={X} size={16} />
                  </Button>
                </li>
              ))}
            </ul>
            <div>
              <Button
                tone="accent"
                onClick={download}
                startIcon={<Icon icon={Download} size={16} />}
              >
                Download print package
              </Button>
            </div>
          </div>
        )}

        {status && (
          <Typography variant="body-sm" tone="muted" role="status">
            {status}
          </Typography>
        )}
      </div>
    </AppShell>
  );
}
